/**
 * WordPress dependencies
 */
import { store, getContext, getElement, getConfig } from '@wordpress/interactivity';

const QS_KEYS = {
	page: 'agenda_page',
	perPage: 'per_page',
	evenement: 'evenement',
	tags: 'tags',
	cnm: 'cnm',
	start: 'start',
	end: 'end',
};

function clampInt(v, min, max) {
	const n = parseInt(v, 10);
	if (Number.isNaN(n)) return null;
	return Math.min(max, Math.max(min, n));
}

function parseISODate(s) {
	// "YYYY-MM-DD"
	if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
	const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
	const dt = new Date(y, m - 1, d);
	return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatISO(date) {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

function formatDDMMYYYY(date) {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
	const pad = (n) => String(n).padStart(2, '0');
	return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function parseTagsParam(s) {
	if (!s) return [];
	return String(s)
		.split(',')
		.map((x) => x.trim())
		.filter(Boolean);
}

function buildSearchFromState(state, context) {
	const params = new URLSearchParams();

	// pagination
	if (state.page && state.page !== 1) params.set(QS_KEYS.page, String(state.page));
	if (state.perPage && state.perPage !== 40) params.set(QS_KEYS.perPage, String(state.perPage));

	// filters
	if (state.evenementFilters) params.set(QS_KEYS.evenement, String(state.evenementFilters));
	if (state.tagFilters && state.tagFilters.length) params.set(QS_KEYS.tags, state.tagFilters.join(','));
	if (context?.filterCNM) params.set(QS_KEYS.cnm, '1');

	// dates
	if (state.start instanceof Date && !Number.isNaN(state.start.getTime())) {
		params.set(QS_KEYS.start, formatISO(state.start));
	}
	if (state.end instanceof Date && !Number.isNaN(state.end.getTime())) {
		params.set(QS_KEYS.end, formatISO(state.end));
	}

	return params;
}

const { state, actions } = store('cnm-agenda-page', {
	state: {
		// filtres
		tagFilters: [],
		evenementFilters: '',
		evenementFiltersActive: false,
		tagFiltersActive: false,
		hasFilterActive: false,
		hasVisibleCards: true,

		// dates
		start: null,
		end: null,

		// mois sticky
		currentMonth: '',

		// pagination
		perPage: 40,
		page: 1,
		totalPages: 1,
		totalVisible: 0,
		hidePagination: true,
		disablePrev: true,
		disableNext: true,

		// URL sync
		qsApplied: false,
		qsHydrating: false, // ignore events / url sync during hydration
	},

	actions: {
		// ---------------------------------------------------------------------
		// URL sync
		// ---------------------------------------------------------------------
		applyFromQueryString: () => {
			const context = getContext();
			const params = new URLSearchParams(window.location.search);

			state.qsHydrating = true;

			// per page
			const qpPer = clampInt(params.get(QS_KEYS.perPage), 1, 200);
			if (qpPer) state.perPage = qpPer;

			// page
			const qpPage = clampInt(params.get(QS_KEYS.page), 1, 9999);
			if (qpPage) state.page = qpPage;

			// evenement
			const qpEvt = params.get(QS_KEYS.evenement);
			if (qpEvt) {
				state.evenementFilters = qpEvt;
				state.evenementFiltersActive = true;
			} else {
				state.evenementFilters = '';
				state.evenementFiltersActive = false;
			}

			// tags (multi)
			const qpTags = parseTagsParam(params.get(QS_KEYS.tags));
			state.tagFilters = qpTags;
			state.tagFiltersActive = qpTags.length > 0;

			// cnm
			const qpCnm = params.get(QS_KEYS.cnm);
			if (context) context.filterCNM = qpCnm === '1' || qpCnm === 'true';

			// dates ISO
			state.start = parseISODate(params.get(QS_KEYS.start));
			state.end = parseISODate(params.get(QS_KEYS.end));

			state.qsApplied = true;

			actions.applyUiFromState();

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					state.qsHydrating = false;
					actions.filterCards();
				});
			});
		},

		applyFromPhpContext: () => {
			const context = getContext();
			const init = context?.initial;
			if (!init) return false;

			state.qsHydrating = true;

			if (init.perPage) state.perPage = init.perPage;
			if (init.page) state.page = init.page;

			if (init.evenement) {
				state.evenementFilters = init.evenement;
				state.evenementFiltersActive = true;
			} else {
				state.evenementFilters = '';
				state.evenementFiltersActive = false;
			}

			state.tagFilters = Array.isArray(init.tags) ? init.tags : [];
			state.tagFiltersActive = state.tagFilters.length > 0;

			context.filterCNM = !!init.cnm;

			state.start = init.start ? parseISODate(init.start) : null;
			state.end = init.end ? parseISODate(init.end) : null;

			state.qsApplied = true;

			actions.applyUiFromState();

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					state.qsHydrating = false;
					actions.filterCards();
				});
			});

			return true;
		},

		syncUrlFromState: () => {
			if (state.qsHydrating) return;

			const context = getContext();
			const params = buildSearchFromState(state, context);

			const newQs = params.toString();
			const newUrl = newQs
				? `${window.location.pathname}?${newQs}${window.location.hash || ''}`
				: `${window.location.pathname}${window.location.hash || ''}`;

			window.history.replaceState({}, '', newUrl);
		},

		applyUiFromState: () => {
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			if (!root) return;

			requestAnimationFrame(() => {
				// Events
				const evtButtons = root.querySelectorAll('button[data-evenement]');
				evtButtons.forEach((b) => b.classList.remove('active'));
				const evt = state.evenementFilters || '';
				const btnEvt = root.querySelector(`button[data-evenement="${CSS.escape(evt)}"]`);
				if (btnEvt) btnEvt.classList.add('active');
				else root.querySelector('button[data-evenement=""]')?.classList.add('active');

				// Tags: multi
				const tagButtons = root.querySelectorAll('button[data-tag]');
				tagButtons.forEach((b) => b.classList.remove('active'));
				if (!state.tagFilters.length) {
					root.querySelector('button[data-tag=""]')?.classList.add('active');
				} else {
					state.tagFilters.forEach((slug) => {
						root.querySelector(`button[data-tag="${CSS.escape(slug)}"]`)?.classList.add('active');
					});
				}

				// Pickers
				actions.updateFilterButtonsA11y();
				actions.syncPickersFromState();
			});
		},

		syncPickersFromState: async () => {
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			if (!root) return;

			const pickers = Array.from(root.querySelectorAll('duet-date-picker'));
			if (!pickers.length) return;

			const wasHydrating = state.qsHydrating;
			state.qsHydrating = true;

			const setOne = async (picker, isoValue) => {
				// Attendre que Duet soit VRAIMENT prêt
				try {
					if (typeof picker.componentOnReady === 'function') {
						await picker.componentOnReady();
					} else {
						await customElements.whenDefined('duet-date-picker');
					}
				} catch (e) {}

				// ✅ Duet veut ISO
				picker.value = isoValue || '';

				// (optionnel mais souvent utile) forcer un repaint / update interne
				if (typeof picker.forceUpdate === 'function') {
					try { picker.forceUpdate(); } catch (e) {}
				}
			};

			const startIso = state.start ? formatISO(state.start) : '';
			const endIso   = state.end ? formatISO(state.end) : '';

			await Promise.all(
				pickers.map(async (picker) => {
					if (picker.dataset.cnm === 'begin') return setOne(picker, startIso);
					if (picker.dataset.cnm === 'end') return setOne(picker, endIso);
				})
			);

			state.qsHydrating = wasHydrating;
		},


		// ---------------------------------------------------------------------
		// Focus / Scroll
		// ---------------------------------------------------------------------
		focusFirstCard: () => {
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			if (!root) return;

			const targetY = Math.max(0, window.scrollY + root.getBoundingClientRect().top);

			window.scrollTo({ top: targetY, behavior: 'smooth' });

			let frames = 0;
			const maxFrames = 120;

			const tick = () => {
				frames += 1;
				const delta = Math.abs(window.scrollY - targetY);
				const done = delta < 2 || frames >= maxFrames;

				if (!done) {
					requestAnimationFrame(tick);
					return;
				}

				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const firstLink = root.querySelector('.cnm-list > li a');
						if (firstLink) {
							try {
								firstLink.focus({ preventScroll: true });
							} catch (e) {
								firstLink.focus();
							}
						}
						actions.innerScroll();
					});
				});
			};

			requestAnimationFrame(tick);
		},

		scrollTopAfterFilter: () => {
			if (state.qsHydrating) return;
			actions.focusFirstCard();
		},

		// ---------------------------------------------------------------------
		// Pagination
		// ---------------------------------------------------------------------
		updatePagination: () => {
			const context = getContext();
			if (!context || !Array.isArray(context.cards)) return;

			const visibles = context.cards.filter((c) => c && c.visible);

			state.totalVisible = visibles.length;
			state.totalPages = Math.max(1, Math.ceil(visibles.length / state.perPage));

			if (state.page > state.totalPages) state.page = state.totalPages;
			if (state.page < 1) state.page = 1;

			const startIdx = (state.page - 1) * state.perPage;
			const endIdx = startIdx + state.perPage;
			context.pagedCards = visibles.slice(startIdx, endIdx);

			state.hidePagination = state.totalPages === 1;
			state.disablePrev = state.page === 1;
			state.disableNext = state.page === state.totalPages;

			state.hasVisibleCards = visibles.length > 0;

			if (context.pagedCards.length) {
				state.currentMonth = context.pagedCards[0].monthName || state.currentMonth;
			} else {
				state.currentMonth = '';
			}

			// reset sticky month measurement
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			const monthEl = root?.querySelector('.cnm-agenda__current-month');
			const ph = root?.querySelector('.cnm-agenda__month-placeholder');

			if (monthEl) {
				monthEl.classList.remove('fixed');
				monthEl.removeAttribute('data-anchor-y');
			}
			if (ph) {
				ph.style.display = 'none';
				ph.style.height = '0px';
			}

			actions.syncUrlFromState();
			requestAnimationFrame(() => actions.innerScroll());
		},

		nextPage: () => {
			if (state.page < state.totalPages) {
				state.page += 1;
				actions.updatePagination();
				actions.focusFirstCard();
			}
		},

		prevPage: () => {
			if (state.page > 1) {
				state.page -= 1;
				actions.updatePagination();
				actions.focusFirstCard();
			}
		},

		// ---------------------------------------------------------------------
		// Init
		// ---------------------------------------------------------------------
		fetchCards: () => {
			const context = getContext();
			getConfig();

			context.cards = state.cards;
			context.evenements = state.evenements;
			context.thematiques = state.thematiques;

			if (Array.isArray(context.cards)) {
				context.cards.forEach((c, i) => {
					if (typeof context.cards[i].visible === 'undefined') context.cards[i].visible = true;
				});
			}

			if (!state.qsApplied) {
				if (actions.applyFromPhpContext()) return;
				actions.applyFromQueryString();
				return;
			}

			actions.filterCards();
		},

		// ---------------------------------------------------------------------
		// Pickers
		// ---------------------------------------------------------------------
		setPickers: () => {
			// ✅ wait duet component defined to avoid losing initial value on refresh
			customElements.whenDefined('duet-date-picker').then(() => {
				const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
				const pickers = root ? root.querySelectorAll('duet-date-picker') : document.querySelectorAll('duet-date-picker');

				const DATE_FR = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
				const DATE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

				pickers.forEach((picker) => {
					picker.addEventListener('duetChange', (e) => {
						if (state.qsHydrating) return;

						state.page = 1;

						if (picker.dataset.cnm === 'begin') state.start = e.detail.valueAsDate;
						else if (picker.dataset.cnm === 'end') state.end = e.detail.valueAsDate;

						actions.filterCards();
						requestAnimationFrame(() => actions.scrollTopAfterFilter());
					});

					picker.dateAdapter = {
						parse(value = '', createDate) {
							const fr = value.match(DATE_FR);
							if (fr) return createDate(fr[3], fr[2], fr[1]);

							const iso = value.match(DATE_ISO);
							if (iso) return createDate(iso[1], iso[2], iso[3]);

							return undefined;
						},
						format(date) {
							// display in FR
							return formatDDMMYYYY(date);
						},
					};

					picker.localization = {
						buttonLabel: 'Choisir une date',
						placeholder: 'jj/mm/aaaa',
						selectedDateMessage: 'La date sélectionnée est',
						prevMonthLabel: 'Mois précédent',
						nextMonthLabel: 'Mois suivant',
						monthSelectLabel: 'Mois',
						yearSelectLabel: 'Année',
						closeLabel: 'Fermer la fenêtre',
						calendarHeading: 'Choisir une date',
						dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
						monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
						monthNamesShort: ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'],
						locale: 'fr-FR',
					};
				});

				// ✅ inject hydrated values (ISO) once duet is ready
				actions.syncPickersFromState();
			});
		},

		// ---------------------------------------------------------------------
		// Filters
		// ---------------------------------------------------------------------
		filterCards: () => {
			const context = getContext();
			if (!context || !Array.isArray(context.cards)) return;

			const start = state.start;
			const end = state.end;

			context.cards.forEach((card, key) => {
				const date = moment(card.dateDebut);

				const dateInRange = (() => {
					const hasStart = start && moment(start).isValid();
					const hasEnd = end && moment(end).isValid();
					if (!hasStart && !hasEnd) return true;
					if (hasStart && hasEnd) return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
					if (hasStart) return date.isSameOrAfter(start, 'day');
					return date.isSameOrBefore(end, 'day');
				})();

				const tagsMatch = !state.tagFiltersActive || card.thematiques.some((tag) => state.tagFilters.includes(tag));
				const evenementMatch = !state.evenementFiltersActive || card.evenements.some((e) => state.evenementFilters == e);

				if (context.filterCNM) {
					const isCNM = card.classes.includes('is-cnm');
					context.cards[key].visible = dateInRange && isCNM && evenementMatch && tagsMatch;
				} else {
					context.cards[key].visible = dateInRange && evenementMatch && tagsMatch;
				}
			});

			actions.updatePagination();
		},

		checkActiveFilters: () => {
			const context = getContext();
			state.hasFilterActive = !!(
				state.evenementFiltersActive ||
				state.tagFiltersActive ||
				context.filterCNM ||
				state.start ||
				state.end
			);
		},

		filterDatesCNM: () => {
			const context = getContext();
			context.filterCNM = !context.filterCNM;

			state.page = 1;
			actions.filterCards();
			actions.applyUiFromState();
			requestAnimationFrame(() => actions.scrollTopAfterFilter());
		},

		updateFilterButtonsA11y: () => {
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			if (!root) return;

			// Événements
			const evtButtons = root.querySelectorAll('button[data-evenement]');
			evtButtons.forEach((button) => {
				const value = button.getAttribute('data-evenement') || '';
				const isPressed = value === ''
					? !state.evenementFiltersActive
					: state.evenementFilters === value;

				button.classList.toggle('active', isPressed);
				button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
			});

			// Thématiques
			const tagButtons = root.querySelectorAll('button[data-tag]');
			tagButtons.forEach((button) => {
				const value = button.getAttribute('data-tag') || '';
				const isPressed = value === ''
					? !state.tagFiltersActive
					: state.tagFilters.includes(value);

				button.classList.toggle('active', isPressed);
				button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
			});
		},

		filterEvenements: () => {
			const element = getElement();
			const evenementFilter = element.attributes['data-evenement'];

			state.page = 1;

			if (element.ref.classList.contains('active')) {
				element.ref.classList.remove('active');
				state.evenementFilters = '';
			} else {
				state.evenementFilters = evenementFilter;
			}

			if (!state.evenementFilters || evenementFilter === '') {
				state.evenementFiltersActive = false;
				state.evenementFilters = '';
			} else {
				state.evenementFiltersActive = true;
			}

			actions.filterCards();
			actions.updateFilterButtonsA11y();
			actions.applyUiFromState();
			requestAnimationFrame(() => actions.scrollTopAfterFilter());
		},

		filterTags: () => {
			const element = getElement();
			const tagFilter = element.attributes['data-tag'];

			state.page = 1;

			if (!tagFilter) {
				state.tagFilters = [];
				state.tagFiltersActive = false;
				actions.filterCards();
				actions.updateFilterButtonsA11y();
				actions.applyUiFromState();
				requestAnimationFrame(() => actions.scrollTopAfterFilter());
				return;
			}

			const idx = state.tagFilters.indexOf(tagFilter);
			if (idx > -1) state.tagFilters.splice(idx, 1);
			else state.tagFilters.push(tagFilter);

			state.tagFiltersActive = state.tagFilters.length > 0;

			actions.filterCards();
			actions.updateFilterButtonsA11y();
			actions.applyUiFromState();
			requestAnimationFrame(() => actions.scrollTopAfterFilter());
		},

		reset: () => {
			const context = getContext();
			if (!context) return;

			state.page = 1;

			state.hasFilterActive = false;
			state.start = null;
			state.end = null;

			context.filterCNM = false;

			state.tagFilters = [];
			state.tagFiltersActive = false;
			state.evenementFilters = '';
			state.evenementFiltersActive = false;

			const wasHydrating = state.qsHydrating;
			state.qsHydrating = true;
			document.querySelectorAll('duet-date-picker').forEach((picker) => (picker.value = ''));
			state.qsHydrating = wasHydrating;

			context.cards.forEach((card, key) => (context.cards[key].visible = true));

			actions.updatePagination();
			actions.updateFilterButtonsA11y();
			actions.applyUiFromState();
			requestAnimationFrame(() => actions.scrollTopAfterFilter());
		},

		// ---------------------------------------------------------------------
		// Sticky month + month name
		// ---------------------------------------------------------------------
		innerScroll: () => {
			const root = document.querySelector('.wp-block-cnm-cnm-agenda-page');
			if (!root) return;

			const currentMonthElement = root.querySelector('.cnm-agenda__current-month');
			if (!currentMonthElement) return;

			const placeholder = root.querySelector('.cnm-agenda__month-placeholder');

			if (!currentMonthElement.dataset.anchorY) {
				const wasFixedForMeasure = currentMonthElement.classList.contains('fixed');
				if (wasFixedForMeasure) currentMonthElement.classList.remove('fixed');

				const r = currentMonthElement.getBoundingClientRect();
				currentMonthElement.dataset.anchorY = String(window.scrollY + r.top);

				if (wasFixedForMeasure) currentMonthElement.classList.add('fixed');
			}

			const anchorY = parseFloat(currentMonthElement.dataset.anchorY);
			const wasFixed = currentMonthElement.classList.contains('fixed');
			const shouldFix = window.scrollY >= anchorY;

			if (placeholder) {
				if (shouldFix && !wasFixed) {
					const h = currentMonthElement.getBoundingClientRect().height;
					placeholder.style.display = 'block';
					placeholder.style.height = `${h}px`;
				} else if (!shouldFix && wasFixed) {
					placeholder.style.display = 'none';
					placeholder.style.height = '0px';
				}
			}

			currentMonthElement.classList.toggle('fixed', shouldFix);

			const items = root.querySelectorAll('.cnm-list > li');
			const last = items.length ? items[items.length - 1] : null;

			const monthRect = currentMonthElement.getBoundingClientRect();
			const cutoff = currentMonthElement.classList.contains('fixed')
				? monthRect.height + 1
				: monthRect.bottom + 1;

			if (last && last.getBoundingClientRect().bottom <= cutoff) {
				if (currentMonthElement.classList.contains('fixed')) {
					currentMonthElement.classList.remove('fixed');
					if (placeholder) {
						placeholder.style.display = 'none';
						placeholder.style.height = '0px';
					}
				}
				return;
			}

			for (const li of items) {
				if (li.getBoundingClientRect().top >= cutoff) {
					const m = li.dataset.monthName || li.getAttribute('data-month-name') || '';
					if (m && m !== state.currentMonth) state.currentMonth = m;
					break;
				}
			}
		},
	},
});
